import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { MANUFACTURERS, DEVICE_TYPES, getModels, PROBLEMS } from "../data/repairCatalog";
import { CaretLeft, X, MagnifyingGlass } from "@phosphor-icons/react";

/**
 * Brand → Device → Model → Issue picker — used for adding/editing a priced
 * repair line on an existing ticket.
 *
 * Now sources BOTH the website catalog (`/catalog/website`) and the local
 * repair catalog (`/repair-catalog`) so it matches the NewTicketWizard
 * experience.
 */
export default function CatalogItemPicker({ open, onClose, onSelect }) {
  const [step, setStep] = useState(0);
  const [catalog, setCatalog] = useState({ manufacturer: [], device_type: [], model: [], problem: [] });
  const [websiteCatalog, setWebsiteCatalog] = useState(null);
  const [websiteCatalogReady, setWebsiteCatalogReady] = useState(false);
  const [brand, setBrand] = useState(null);       // { id, label, _slug? }
  const [deviceType, setDeviceType] = useState(null); // { id, label }
  const [model, setModel] = useState("");          // e.g. "Galaxy S20 FE"
  const [modelMeta, setModelMeta] = useState({ year: "", model_number: "" });
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setBrand(null);
    setDeviceType(null);
    setModel("");
    setModelMeta({ year: "", model_number: "" });
    setQuery("");
    setWebsiteCatalogReady(false);
    api.get("/repair-catalog").then(({ data }) => setCatalog(data)).catch(() => {});
    api.get("/catalog/website")
      .then(({ data }) => setWebsiteCatalog(data))
      .catch(() => setWebsiteCatalog(null))
      .finally(() => setWebsiteCatalogReady(true));
  }, [open]);

  const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "");

  // ── Step 0: Brands ──
  const brands = useMemo(() => {
    const map = new Map();
    // Website catalog
    (websiteCatalog?.manufacturers || []).forEach((m) => {
      map.set(norm(m.name), { id: m.slug || norm(m.name), label: m.name, _slug: m.slug });
    });
    // Static catalog
    MANUFACTURERS.forEach((m) => {
      if (!map.has(norm(m.label))) map.set(norm(m.label), { id: m.id, label: m.label });
    });
    // Local catalog
    (catalog.manufacturer || []).filter((c) => !c.hidden).forEach((c) => {
      if (!map.has(norm(c.label))) map.set(norm(c.label), { id: c.id, label: c.label, _custom: true });
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [websiteCatalog, catalog.manufacturer]);

  // ── Step 1: Device Types (Phone, Tablet, Laptop, etc.) ──
  const deviceTypes = useMemo(() => {
    if (!brand) return [];
    const brandKey = norm(brand.label);
    const map = new Map();
    // Website catalog
    const wMfr = (websiteCatalog?.manufacturers || []).find((m) => norm(m.name) === brandKey);
    if (wMfr) {
      (wMfr.device_types || []).forEach((dt) => {
        map.set(norm(dt.name), { id: dt.slug || norm(dt.name), label: dt.name, _wdt: dt });
      });
    }
    // Static catalog
    const staticMfr = MANUFACTURERS.find((m) => norm(m.label) === brandKey);
    if (staticMfr) {
      DEVICE_TYPES.forEach((dt) => {
        const key = `${staticMfr.id}|${dt.id}`;
        if (getModels(staticMfr.id, dt.id)?.length > 0 && !map.has(norm(dt.label))) {
          map.set(norm(dt.label), { id: dt.id, label: dt.label });
        }
      });
    }
    // Local catalog
    (catalog.device_type || []).filter((c) => !c.hidden)
      .filter((c) => !c.parent_manufacturer || c.parent_manufacturer === brand.id)
      .forEach((c) => {
        if (!map.has(norm(c.label))) map.set(norm(c.label), { id: c.id, label: c.label, _custom: true });
      });
    if (map.size === 0) {
      DEVICE_TYPES.forEach((dt) => map.set(norm(dt.label), { id: dt.id, label: dt.label }));
    }
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [brand, websiteCatalog, catalog.device_type]);

  // ── Step 2: Models (Galaxy S20 FE, iPhone 15 Pro, etc.) ──
  const models = useMemo(() => {
    if (!brand) return [];
    const brandKey = norm(brand.label);
    const typeKey = deviceType ? norm(deviceType.label) : "";
    const map = new Map();
    // Website catalog — THE real device list
    const wMfr = (websiteCatalog?.manufacturers || []).find((m) => norm(m.name) === brandKey);
    if (wMfr) {
      (wMfr.device_types || []).forEach((dt) => {
        if (typeKey && norm(dt.name) !== typeKey) return;
        (dt.models || []).forEach((md) => {
          const yearStr = md.year || "";
          const modelNumStr = md.model_number || "";
          const displayLabel = [md.name, yearStr, modelNumStr].filter(Boolean).join(" · ");
          map.set(norm(md.name), { label: displayLabel, name: md.name, year: yearStr, model_number: modelNumStr });
        });
      });
    }
    // Static catalog
    const staticMfr = MANUFACTURERS.find((m) => norm(m.label) === brandKey);
    if (staticMfr) {
      DEVICE_TYPES.forEach((dt) => {
        if (typeKey && norm(dt.label) !== typeKey) return;
        (getModels(staticMfr.id, dt.id) || []).forEach((m) => {
          const lbl = typeof m === "string" ? m : m.label;
          if (!map.has(norm(lbl))) map.set(norm(lbl), { label: lbl, name: lbl, year: "", model_number: "" });
        });
      });
    }
    // Local catalog
    (catalog.model || []).filter((c) => !c.hidden)
      .filter((c) => c.parent_manufacturer === brand?.id && (!typeKey || c.parent_device_type === deviceType?.id))
      .forEach((c) => {
        if (!map.has(norm(c.label))) map.set(norm(c.label), { label: c.label, name: c.label, year: "", model_number: "" });
      });
    let all = Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      all = all.filter((m) => m.label.toLowerCase().includes(q));
    }
    return all;
  }, [brand, deviceType, websiteCatalog, catalog.model, query]);

  // ── Step 3: Issues / Repairs ──
  const issues = useMemo(() => {
    const brandKey = norm(brand?.label || "");
    const typeKey = norm(deviceType?.label || "");
    const websiteIssues = [];
    // The website catalog is authoritative for a model that exists there.
    // Flatten each repair grade into its actual customer-facing choice so the
    // editor uses the same repair names and prices as the booking website.
    const wMfr = (websiteCatalog?.manufacturers || []).find((m) => norm(m.name) === brandKey);
    if (wMfr) {
      (wMfr.device_types || []).forEach((dt) => {
        if (typeKey && norm(dt.name) !== typeKey) return;
        (dt.models || []).forEach((md) => {
          if (norm(md.name) !== norm(model)) return;
          (md.repairs || []).forEach((r) => {
            const baseStockKey = `web_${md.id}::${r.id}`;
            const options = Array.isArray(r.part_options) ? r.part_options : [];
            if (options.length) {
              options.forEach((option) => websiteIssues.push({
                id: `w_${r.id}_${option.id}`,
                label: `${r.name} · ${option.label}`,
                price: Number(option.price || 0),
                stock_key: options.length > 1 ? `${baseStockKey}::opt:${option.id}` : baseStockKey,
                _website: true,
              }));
            } else {
              websiteIssues.push({ id: `w_${r.id}`, label: r.name, price: Number(r.price || 0), stock_key: baseStockKey, _website: true });
            }
          });
        });
      });
    }
    if (websiteIssues.length > 0) return websiteIssues;
    // Wait until the live website catalog has returned before showing legacy
    // static entries; otherwise the wrong repair list briefly appears first.
    if (!websiteCatalogReady) return [];

    const list = [];
    // Static catalog
    const otherProblem = PROBLEMS.find((p) => p.id === "other");
    PROBLEMS.filter((p) => p.id !== "other").forEach((p) => {
      if (!list.some((l) => norm(l.label) === norm(p.label))) {
        list.push({ id: p.id, label: p.label, price: p.price || 0, stock_key: p.id });
      }
    });
    // Local catalog
    (catalog.problem || []).filter((c) => !c.hidden && !c.override_of).forEach((c) => {
      const mfr = c.parent_manufacturer || "";
      const dev = c.parent_device_type || "";
      if (mfr && norm(mfr) !== norm(brand?.id || "")) return;
      if (dev && dev !== "all" && norm(dev) !== norm(deviceType?.id || "")) return;
      if (!list.some((l) => norm(l.label) === norm(c.label))) {
        list.push({ id: c.id, label: c.label, price: c.price || 0, stock_key: c.id, _custom: true });
      }
    });
    if (otherProblem) list.push(otherProblem);
    return list;
  }, [brand, deviceType, model, websiteCatalog, websiteCatalogReady, catalog.problem]);

  if (!open) return null;

  const deviceLabel = [
    brand?.label,
    deviceType?.label && deviceType.label !== "Other" ? deviceType.label : "",
    model,
  ].filter(Boolean).join(" ");

  // Deduplicate brand from model if model already starts with brand
  const cleanDeviceLabel = (() => {
    const parts = [];
    const brandStr = (brand?.label || "").trim();
    const dtStr = (deviceType?.label || "").trim();
    const modelStr = (model || "").trim();
    if (brandStr) parts.push(brandStr);
    if (dtStr && dtStr !== "Other") parts.push(dtStr);
    if (modelStr) {
      const joined = parts.join(" ").toLowerCase();
      if (modelStr.toLowerCase().startsWith(joined)) {
        return modelStr;
      }
      if (brandStr && modelStr.toLowerCase().startsWith(brandStr.toLowerCase())) {
        return [modelStr, dtStr && dtStr !== "Other" ? dtStr : ""].filter(Boolean).join(" ");
      }
    }
    return parts.concat(modelStr ? [modelStr] : []).join(" ");
  })();

  const handlePickIssue = (issue) => {
    const desc = issue.id === "other"
      ? (cleanDeviceLabel || "Repair")
      : `${cleanDeviceLabel ? cleanDeviceLabel + " — " : ""}${issue.label}`;
    onSelect({
      description: desc,
      unit_price: issue.price || 0,
      quantity: 1,
      device_label: cleanDeviceLabel || null,
      brand_label: brand?.label || null,
      device_type_label: deviceType?.label && deviceType.label !== "Other" ? deviceType.label : null,
      model: model || null,
      model_year: modelMeta.year || null,
      model_number: modelMeta.model_number || null,
      issue_label: issue.id === "other" ? null : issue.label,
      stock_key: issue.stock_key || null,
    });
  };

  const stepTitles = ["Pick a brand", "Pick device type", "Pick the model", "Pick the repair"];
  const breadcrumb = [brand?.label, deviceType?.label, model].filter(Boolean).join(" > ");

  return (
    <div className="fixed inset-0 z-[10050] bg-slate-900/70 flex items-center justify-center p-3" onClick={onClose} data-testid="catalog-picker-overlay">
      <div
        className="bg-white w-full max-w-lg rounded-3xl shadow-2xl border border-slate-200 overflow-hidden max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {step > 0 && (
              <button
                onClick={() => { setStep((s) => s - 1); setQuery(""); }}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600"
                data-testid="catalog-picker-back"
              >
                <CaretLeft size={14} weight="bold" />
              </button>
            )}
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#9CA3AF] font-semibold">
                {breadcrumb || "Add from catalog"}
              </div>
              <h3 className="font-heading text-lg font-bold text-[#121216]">{stepTitles[step]}</h3>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500"
            data-testid="catalog-picker-close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {/* Step 0: Brands */}
          {step === 0 && (
            <div className="grid grid-cols-3 gap-2">
              {brands.map((b) => (
                <button
                  key={b.id}
                  onClick={() => { setBrand(b); setStep(1); setQuery(""); }}
                  className="h-16 rounded-xl border border-slate-200 hover:border-[#1E5FEB] hover:bg-blue-50/50 flex items-center justify-center text-[13px] font-semibold text-[#121216] transition-colors"
                  data-testid={`catalog-brand-${b.id}`}
                >
                  {b.label}
                </button>
              ))}
            </div>
          )}

          {/* Step 1: Device Types */}
          {step === 1 && (
            <div className="grid grid-cols-3 gap-2">
              {deviceTypes.map((d) => (
                <button
                  key={d.id}
                  onClick={() => { setDeviceType(d); setStep(2); setQuery(""); }}
                  className="h-16 rounded-xl border border-slate-200 hover:border-[#1E5FEB] hover:bg-blue-50/50 flex items-center justify-center text-[13px] font-semibold text-[#121216] transition-colors"
                  data-testid={`catalog-device-${d.id}`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          )}

          {/* Step 2: Models — now includes website catalog real device names */}
          {step === 2 && (
            <div>
              <div className="relative mb-3">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search or type a model..."
                  className="w-full h-10 pl-9 pr-3 border border-slate-200 rounded-xl text-[13px] focus:border-[#1E5FEB] focus:outline-none"
                  data-testid="catalog-model-search"
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-2 gap-2 max-h-72 overflow-y-auto">
                {models.map((m) => (
                  <button
                    key={m.name}
                    onClick={() => {
                      setModel(m.name);
                      setModelMeta({ year: m.year || "", model_number: m.model_number || "" });
                      setStep(3);
                      setQuery("");
                    }}
                    className="h-11 px-3 rounded-xl border border-slate-200 hover:border-[#1E5FEB] hover:bg-blue-50/50 text-[13px] font-medium text-[#121216] text-left truncate transition-colors"
                    data-testid="catalog-model-option"
                  >
                    {m.label}
                  </button>
                ))}
                {query.trim() && !models.some((m) => m.name.toLowerCase() === query.trim().toLowerCase()) && (
                  <button
                    onClick={() => {
                      setModel(query.trim());
                      setModelMeta({ year: "", model_number: "" });
                      setStep(3);
                      setQuery("");
                    }}
                    className="h-11 px-3 rounded-xl border border-dashed border-[#1E5FEB] text-[13px] font-medium text-[#1E5FEB] text-left truncate"
                    data-testid="catalog-model-custom"
                  >
                    Use "{query.trim()}"
                  </button>
                )}
                {!query.trim() && (
                  <button
                    onClick={() => { setModel(""); setModelMeta({ year: "", model_number: "" }); setStep(3); }}
                    className="h-11 px-3 rounded-xl border border-dashed border-slate-300 text-[13px] font-medium text-slate-500 text-left truncate"
                    data-testid="catalog-model-skip"
                  >
                    Skip model
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Issues / Repairs */}
          {step === 3 && (
            <div className="grid grid-cols-1 gap-1.5">
              {!websiteCatalogReady && <div className="py-10 text-center text-sm text-slate-400" data-testid="catalog-issues-loading">Loading website repairs…</div>}
              {websiteCatalogReady && issues.map((p) => (
                <button
                  key={p.id}
                  onClick={() => handlePickIssue(p)}
                  className="flex items-center justify-between h-12 px-4 rounded-xl border border-slate-200 hover:border-[#1E5FEB] hover:bg-blue-50/50 transition-colors"
                  data-testid={`catalog-issue-${p.id}`}
                >
                  <span className="text-[13px] font-medium text-[#121216]">{p.label}</span>
                  {p.price > 0 && <span className="font-mono text-[13px] font-semibold text-[#1E5FEB]">${p.price.toFixed(2)}</span>}
                </button>
              ))}
              {websiteCatalogReady && issues.length === 0 && <div className="py-10 text-center text-sm text-slate-400" data-testid="catalog-issues-empty">No repairs found for this model.</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
